import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import Flex from '../Flex';
import MoreMenu from '../MoreMenu';
import ListCardView from '../ListCardView';
import { handleSort } from '../../helpers/sort';
import { media, breakpoints } from '../../utils/media';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, useMediaQuery } from '@mui/material';

const TableContainerStyled = styled(TableContainer)`
  && {
    -webkit-overflow-scrolling: touch;
    border-radius: 16px;
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

const TableStyled = styled(({ fixed, ...props }) => <Table {...props} />)`
  thead,
  tbody {
    th,
    td {
      font-family: ${(props) => props.theme.fonts.TEXT_FONT}, sans-serif;
      color: ${(props) => props.theme.colors.NEU_PRIMARY};
      font-weight: 300;
      line-height: 18px;
      font-size: ${(props) => props.theme.fontSizes.small};
      padding: 8px;
    }
    th:first-child,
    td:first-child {
      position: sticky;
      left: 0;
      z-index: 2;
      background-color: inherit;
      padding-left: 24px;
    }
    th:last-child,
    td:last-child {
      right: 0;
      z-index: 2;
      background-color: inherit;
      padding-right: 24px;
    }
  }
  thead th {
    padding: 16px 8px;
  }
  thead tr {
    background-color: ${(props) => props.theme.colors.NEU_50};
  }
  thead th {
    color: ${(props) => props.theme.colors.NEU_600};
  }
  tbody tr {
    background-color: ${(props) => props.theme.colors.NEU_0};
  }
  ${({ fixed }) =>
    !!fixed &&
    `
    th:nth-child(1),
    td:nth-child(1) {
      width: 30%;
    }

    th:nth-child(2),
    td:nth-child(2) {
      width: 20%;
    }

    th:nth-child(3),
    td:nth-child(3) {
      width: 15%;
    }

    th:nth-child(4),
    td:nth-child(4) {
      width: 20%;
    }

    th:nth-child(5),
    td:nth-child(5) {
      width: 20%;
    }
  `}
  ${media.lessThan('phablet')} {
    thead,
    tbody {
      th,
      td {
        padding: 8px 10px;
        font-size: ${(props) => props.theme.fontSizes.xSmall};
      }
    }
  }
`;
const TableCellTitle = styled(TableCell)`
  text-transform: uppercase;
`;

const TableRowStyled = styled(TableRow)`
  ${(props) =>
    props.$hasClick === 'true' &&
    `
      cursor: pointer;
      &:hover {
        background-color: ${props.theme.colors.NEU_50};
      }
  `}
`;

const ListTable = ({
  alignLeft = 0,
  tableInfo,
  data,
  onRowClick,
  tableName,
  fixed = false,
  tableSetter,
  listDataProp,
  additionalData,
  defaultRowPerPage = 25,
  defaultStartPage = 0,
  keepTrackOfPage,
}) => {
  const isMobile = useMediaQuery(`(max-width:${breakpoints.phablet - 1}px)`);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState();
  const [page, setPage] = useState(defaultStartPage);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowPerPage);
  const [editableData, setEditableData] = useState([]);

  const clickRowHandler = useCallback(
    (e, item) => {
      e.preventDefault();
      e.stopPropagation();
      onRowClick(item);
    },
    [onRowClick],
  );

  const onClick = useCallback(
    (label, hasSorting, handleClick) => {
      let LocalOrder;
      if (orderBy !== label && hasSorting?.initialOrder) {
        LocalOrder = hasSorting.initialOrder;
      } else {
        LocalOrder = order === 'desc' ? 'asc' : 'desc';
      }
      setOrderBy(label);
      setOrder(LocalOrder);

      if (hasSorting && !handleClick) {
        return handleSort({
          sortedArr: data,
          resultSetter: setEditableData,
          tableSetter,
          additionalData,
          listDataProp,
          sorting: hasSorting,
          order: LocalOrder,
        });
      }
      return handleClick && handleClick();
    },
    [data, tableSetter, additionalData, listDataProp, order, orderBy],
  );

  useEffect(() => {
    // Check if any cell has defaultSorting
    const defaultSortingCell = tableInfo.find((cell) => cell.hasOwnProperty('defaultSorting') && cell.defaultSorting);

    if (defaultSortingCell) {
      onClick(defaultSortingCell.label, defaultSortingCell.hasSorting, null);
    } else {
      setEditableData(Array.isArray(data) ? data : []);
    }

    // Reset to first page on any data change (search, filter, refresh) so the user
    // never lands on a stale/empty page after filtering.
    setPage(0);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    //Page reloads after saving feedback.
    // this allows to keep track of the page teacher was in before the update
    if (keepTrackOfPage && typeof keepTrackOfPage === 'function') {
      keepTrackOfPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  // const emptyRows =
  //   page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const paginatedData = editableData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Flex direction='column' width='100%'>
      {isMobile ? (
        <ListCardView
          data={paginatedData}
          tableInfo={tableInfo}
          tableName={tableName}
          alignLeft={alignLeft}
          onItemClick={onRowClick ? clickRowHandler : undefined}
        />
      ) : (
        <TableContainerStyled component={Paper}>
          <TableStyled sx={{ minWidth: 650 }} aria-label={tableName} fixed={fixed}>
            <TableHead>
              <TableRow>
                {tableInfo.map((headCell, i) => (
                  <TableCellTitle
                    onClick={() => onClick(headCell.label, headCell?.hasSorting, headCell?.handleClick)}
                    key={`${tableName}-${headCell.label}`}
                    align={i === alignLeft ? 'left' : 'center'}
                  >
                    {headCell?.hasSorting || headCell?.handleClick ? (
                      <TableSortLabel active={orderBy === headCell.label} direction={orderBy === headCell.label ? order : 'asc'}>
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCellTitle>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData?.map((item) => (
                <TableRowStyled
                  key={`${tableName}-${item.id}`}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  onClick={(e) => onRowClick && clickRowHandler(e, item)}
                  $hasClick={(!!onRowClick).toString()}
                >
                  {tableInfo.map((row, index) => {
                    const menuOptions = !!row.menuOptions && row.menuOptions(item);
                    const rowData = !!row.data ? row.data(item) : menuOptions?.length ? <MoreMenu item={item} key={item.id} options={menuOptions} /> : '-';
                    return (
                      <TableCell
                        key={`${tableName}-${row.label}- ${item.id}`}
                        component={index === 0 ? 'th' : ''}
                        scope={index === 0 ? 'row' : ''}
                        align={index === alignLeft ? 'left' : 'center'}
                      >
                        {rowData}
                      </TableCell>
                    );
                  })}
                </TableRowStyled>
              ))}
            </TableBody>
          </TableStyled>
        </TableContainerStyled>
      )}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component='div'
        count={data?.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Flex>
  );
};

export default ListTable;
